import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections';
import { WhenLoaded } from '@openx/components-core/lib/Loader/WhenLoaded';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import type { DealApi } from '@openx/types/deal';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { DealDiscount } from '../../components/Discounts/DealDiscount';
import { useDealContext } from '../../utils/context/DealContext';

export type DiscountsDetailsSectionDeal = Pick<DealApi, 'curated_deal_buyer_discount_enabled' | 'deal_participants'>;

type DiscountsDetailsSectionProps<T extends DiscountsDetailsSectionDeal> = {
  deal: T;
  historyMode?: boolean;
};

export const DiscountsDetailsSection = <T extends DiscountsDetailsSectionDeal>({
  deal,
  historyMode,
}: DiscountsDetailsSectionProps<T>) => {
  const { useDealDiscounts, useDemandPartnersFetch } = useDealContext();
  const { t } = useTranslation();

  const participants = useMemo(() => {
    return deal.deal_participants.map(({ buyer_id, buyer_ids, demand_partner, main_buyer_id }) => ({
      demandPartnerId: demand_partner,
      receivedSeatIds: [buyer_id, ...Object.keys(buyer_ids ?? {}), main_buyer_id].filter(Boolean) as string[],
    }));
  }, [deal]);

  const { data, isLoading } = useDealDiscounts(participants);
  const { loading: isLoadingDemandPartnerOptions, mapById: demandPartnerById } = useDemandPartnersFetch();

  const isAnythingLoading = isLoading || isLoadingDemandPartnerOptions;
  const hasAnyDiscountOrIsLoading = isAnythingLoading || data.length !== 0;
  const hasDiscountsPreview = deal.curated_deal_buyer_discount_enabled && !historyMode;

  return (
    <FormSection divider historyMode={historyMode} rightColumnSize={RIGHT_COLUMN_WIDTH + 1} title={t('Discounts')}>
      <FieldDetails
        toggle
        title={t('Enable Discounts for this Deal')}
        value={deal.curated_deal_buyer_discount_enabled}
      />

      {hasDiscountsPreview && (
        <IndentedSections>
          <Alert severity="info">
            {t('Your buyer discounts will be applied to this deal. You can manage them')}{' '}
            <Link target="_blank" to="/discounts">
              {t('here')}
            </Link>
            . {t('You can also disable all of them for this deal.')}
          </Alert>

          <Stack
            spacing={1.5}
            sx={{
              mt: hasAnyDiscountOrIsLoading ? 1.5 : 0,
            }}
          >
            <WhenLoaded loading={isAnythingLoading} noPadding>
              {data.map(datum => (
                <DealDiscount
                  bannerAndVideoDiscount={datum.banner_and_video_discount}
                  ctvDiscount={datum.ctv_discount}
                  demandPartnerById={demandPartnerById}
                  demandPartnerId={datum.demand_partner_id}
                  key={datum.id}
                  name={datum.name}
                  seatIds={datum.seat_ids}
                />
              ))}
            </WhenLoaded>
          </Stack>
        </IndentedSections>
      )}
    </FormSection>
  );
};
